import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Drawer,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import { IconCheck, IconPlus, IconX } from '@tabler/icons-react';
import { apiClient } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { useEmployees } from './EmployeesPage';

// Phase HR-2b UI: الإجازات — request, then approve/reject inline.

interface LeaveRequestDto {
  leaveRequestId: string;
  employeeId: string;
  employeeName: string;
  type: number;
  fromDate: string;
  toDate: string;
  days: number;
  status: number;
  reason: string | null;
}

const QK = ['leave'] as const;

export function LeavePage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('1');
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: [...QK, statusFilter] as const,
    queryFn: async () =>
      (
        await apiClient.get<LeaveRequestDto[]>('/api/hr/leave', {
          params: statusFilter === 'all' ? {} : { status: statusFilter },
        })
      ).data,
  });

  const decide = useMutation({
    mutationFn: async ({ id, approve }: { id: string; approve: boolean }) =>
      (await apiClient.post(`/api/hr/leave/${id}/decide`, { approve })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
    onError: (err) =>
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      }),
  });

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.hr')}
        title={t('hr.leave.title')}
        subtitle={t('hr.leave.subtitle')}
      />

      <Paper p="md">
        <Group justify="space-between">
          <SegmentedControl
            value={statusFilter}
            onChange={setStatusFilter}
            data={[
              { value: '1', label: t('hr.leave.status.pending') },
              { value: '2', label: t('hr.leave.status.approved') },
              { value: '3', label: t('hr.leave.status.rejected') },
              { value: 'all', label: t('hr.leave.all') },
            ]}
          />
          <Button leftSection={<IconPlus size={16} stroke={1.6} />} onClick={() => setCreateOpen(true)}>
            {t('hr.leave.create')}
          </Button>
        </Group>
      </Paper>

      {isLoading ? (
        <Stack align="center" py={48}><Loader size="sm" /></Stack>
      ) : (
        <Paper p={0}>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('hr.leave.columns.employee')}</Table.Th>
                <Table.Th>{t('hr.leave.columns.type')}</Table.Th>
                <Table.Th>{t('hr.leave.columns.period')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('hr.leave.columns.days')}</Table.Th>
                <Table.Th>{t('hr.leave.columns.status')}</Table.Th>
                <Table.Th style={{ width: 140 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(data ?? []).map((l) => (
                <Table.Tr key={l.leaveRequestId}>
                  <Table.Td><Text size="sm" fw={500}>{l.employeeName}</Text></Table.Td>
                  <Table.Td>
                    <Badge size="xs" variant="light" radius="sm"
                      color={l.type === 1 ? 'blue' : l.type === 2 ? 'orange' : 'gray'}>
                      {t(typeKey(l.type))}
                    </Badge>
                  </Table.Td>
                  <Table.Td className="font-numeric">{l.fromDate} ← {l.toDate}</Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>{l.days}</Table.Td>
                  <Table.Td>
                    <Badge size="xs" variant="light" radius="sm"
                      color={l.status === 2 ? 'green' : l.status === 3 ? 'red' : 'yellow'}>
                      {t(statusKey(l.status))}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {l.status === 1 && (
                      <Group gap={4} wrap="nowrap">
                        <Button size="compact-sm" variant="light" color="green"
                          leftSection={<IconCheck size={14} stroke={1.8} />}
                          onClick={() => decide.mutate({ id: l.leaveRequestId, approve: true })}>
                          {t('hr.leave.approve')}
                        </Button>
                        <Button size="compact-sm" variant="subtle" color="red"
                          leftSection={<IconX size={14} stroke={1.8} />}
                          onClick={() => decide.mutate({ id: l.leaveRequestId, approve: false })}>
                          {t('hr.leave.reject')}
                        </Button>
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
              {data && data.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text size="sm" c="dimmed" ta="center" py="lg">{t('hr.leave.empty')}</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <CreateLeaveDrawer
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => qc.invalidateQueries({ queryKey: QK })}
      />
    </Stack>
  );
}

function typeKey(type: number): string {
  switch (type) {
    case 1: return 'hr.leave.type.annual';
    case 2: return 'hr.leave.type.sick';
    default: return 'hr.leave.type.unpaid';
  }
}

function statusKey(status: number): string {
  switch (status) {
    case 2: return 'hr.leave.status.approved';
    case 3: return 'hr.leave.status.rejected';
    default: return 'hr.leave.status.pending';
  }
}

function CreateLeaveDrawer({
  opened, onClose, onCreated,
}: { opened: boolean; onClose: () => void; onCreated: () => void }) {
  const { t, i18n } = useTranslation();
  const employees = useEmployees(false);
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [type, setType] = useState<string>('1');
  const [range, setRange] = useState<[Date | null, Date | null]>([null, null]);
  const [reason, setReason] = useState('');

  const options = useMemo(
    () => (employees.data ?? []).map((e) => ({ value: e.employeeId, label: e.fullNameAr })),
    [employees.data],
  );

  const create = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post('/api/hr/leave', {
          employeeId,
          type: Number(type),
          fromDate: toIsoDate(range[0]!),
          toDate: toIsoDate(range[1] ?? range[0]!),
          reason: reason.trim() || null,
        })
      ).data,
  });

  const valid = employeeId !== null && range[0] !== null;

  const submit = async () => {
    try {
      await create.mutateAsync();
      notifications.show({ color: 'green', title: t('common.toasts.created'), message: '' });
      onCreated();
      onClose();
      setEmployeeId(null); setRange([null, null]); setReason('');
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={t('hr.leave.create')}
      position={i18n.language === 'ar' ? 'left' : 'right'}
      size="md"
      padding="lg"
    >
      <Stack gap="md">
        <Select label={t('hr.leave.columns.employee')} data={options}
          value={employeeId} onChange={setEmployeeId} searchable withAsterisk />
        <SegmentedControl
          value={type}
          onChange={setType}
          data={[
            { value: '1', label: t('hr.leave.type.annual') },
            { value: '2', label: t('hr.leave.type.sick') },
            { value: '3', label: t('hr.leave.type.unpaid') },
          ]}
        />
        <DatePickerInput type="range" allowSingleDateInRange
          label={t('hr.leave.columns.period')} value={range} onChange={setRange} />
        <Textarea label={t('hr.leave.reasonLabel')} rows={2}
          value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={create.isPending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={!valid} loading={create.isPending}>
            {t('common.create')}
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
